import {
  OnboardingHearAboutUs,
  UserPropertiesKeys,
  WebPageEvents,
} from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppStoreIcon,
  ChevronLeft,
  DotsMenu,
  Facebook,
  GoogleSimple,
  Instagram,
  LinkedIn,
  Reddit,
  Threads,
  Tiktok,
  Toolfinder,
  Youtube,
} from 'shared/assets/icons';
import {
  UserSelectAiChip,
  UserSelectFamilyAndFriends,
} from 'shared/assets/icons/user-select';
import { Icon } from 'shared/components/ui/icon';
import { useSetUserProperties } from 'shared/hooks/use-set-user-properties';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';

import { OnboardingHeader } from '../onboarding-header';
import { OnboardingSideImage } from '../onboarding-side-image';
import { ProgressBar } from '../progress-bar';
import * as Styled from './hear-about.style';

const HEAR_ABOUT_IMAGE_URL = '/images/onboarding/hear-about-us.png';

export type HearAboutProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  onSubmit: (value: OnboardingHearAboutUs) => void;
};

export const HearAbout: React.FC<HearAboutProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  onSubmit,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingHearAbout, undefined);
  const setUserProperties = useSetUserProperties();
  const { t } = useTranslation();
  const sources = useMemo(
    () => [
      {
        value: OnboardingHearAboutUs.FriendsOrFamily,
        icon: UserSelectFamilyAndFriends,
        name: t('forms.onboarding.hear-about.friends'),
      },
      {
        value: OnboardingHearAboutUs.AppStore,
        icon: AppStoreIcon,
        name: t('forms.onboarding.hear-about.app-store'),
      },
      {
        value: OnboardingHearAboutUs.AiTools,
        icon: UserSelectAiChip,
        name: t('forms.onboarding.hear-about.ai-tools'),
      },
      {
        value: OnboardingHearAboutUs.Reddit,
        icon: Reddit,
        name: t('forms.onboarding.hear-about.reddit'),
      },
      {
        value: OnboardingHearAboutUs.Toolfinder,
        icon: Toolfinder,
        name: t('forms.onboarding.hear-about.tool-finder'),
      },
      {
        value: OnboardingHearAboutUs.Google,
        icon: GoogleSimple,
        name: t('forms.onboarding.hear-about.google'),
      },
      {
        value: OnboardingHearAboutUs.Instagram,
        icon: Instagram,
        name: t('forms.onboarding.hear-about.instagram'),
      },
      {
        value: OnboardingHearAboutUs.Linkedin,
        icon: LinkedIn,
        name: t('forms.onboarding.hear-about.linkedin'),
      },
      {
        value: OnboardingHearAboutUs.Facebook,
        icon: Facebook,
        name: t('forms.onboarding.hear-about.facebook'),
      },
      {
        value: OnboardingHearAboutUs.Tiktok,
        icon: Tiktok,
        name: t('forms.onboarding.hear-about.tiktok'),
      },
      {
        value: OnboardingHearAboutUs.Threads,
        icon: Threads,
        name: t('forms.onboarding.hear-about.threads'),
      },
      {
        value: OnboardingHearAboutUs.Youtube,
        icon: Youtube,
        name: t('forms.onboarding.hear-about.youtube'),
      },
      {
        value: OnboardingHearAboutUs.Other,
        icon: DotsMenu,
        name: t('forms.onboarding.hear-about.other'),
      },
    ],
    [t],
  );

  const _onSubmit = (value: OnboardingHearAboutUs) => {
    setUserProperties({ [UserPropertiesKeys.HearAbout]: value });
    onSubmit(value);
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />
        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />
          <Styled.Form>
            <Typography variant="h4" component="h1">
              {t('forms.onboarding.hear-about.title')}
            </Typography>
            <Styled.List>
              {sources.map(({ value, name, icon }) => (
                <Styled.ListItem key={value} onClick={() => _onSubmit(value)}>
                  <Styled.ListItemContent>
                    <Styled.ListItemIcon>
                      <Icon icon={icon} />
                    </Styled.ListItemIcon>
                    <Typography variant="subtitle2">{name}</Typography>
                  </Styled.ListItemContent>

                  <Styled.ChevronContainer>
                    <Icon icon={ChevronLeft} />
                  </Styled.ChevronContainer>
                </Styled.ListItem>
              ))}
            </Styled.List>
          </Styled.Form>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <OnboardingSideImage
          imageUrl={HEAR_ABOUT_IMAGE_URL}
          maxImageWidth={318}
          relativeImage
        />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
